import { Component, Input, Output, OnInit, EventEmitter, ViewChild, SimpleChanges, OnChanges, NgZone, AfterViewInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ExportService } from '@app/exportservice/export.service';
import { convertDate } from '@app/finance/finance-utilities';
import {
  LIST_OF_CANDIDATES,
  PASSWORD_TYPES,
} from '@app/operation/operation-constants';
import { getDynamicPassword } from '@app/operation/operation-utilities';
import { Actions, TableColumn } from '@app/shared/table/TableColumn';
import { omit } from 'lodash';
import { UploadComponent } from '../upload/upload.component';
import { MatDialog } from '@angular/material/dialog';
import { ApiCallService } from '@app/services/api-call.service';
import { TableComponent } from '@app/shared/table/table.component';

declare var $: any;

@Component({
  standalone: false,
  selector: 'app-candidatetable',
  templateUrl: './candidatetable.component.html',
  styleUrls: ['./candidatetable.component.css'],
})
export class CandidatetableComponent implements OnInit, OnChanges,AfterViewInit  {
  @Input() dataSource: any[];

  @Input() tableColumns: TableColumn[] = [];
  @Input() tableActions = [];
  @Input() rowActionCallBacks = {};
  @Input() tabelActionCallBacks = {};
  @Input() toggel = {};
  @Input() toggleEmitter;
  @Input() handleToggleInParent: boolean = false;
  @Input() isModal: boolean = false;
  @Input() radioButtonCallBack: boolean = false;
  @Input() selectedItems = [];
  @Input() title: string = 'Candidates';
  @Input() moduleType: string;
  @Input() pageType: string;
  @Input() totalCount: number = 0;
  @Input() serverSidePagination: boolean = false;
  @Input() paginationSizes: number[] = [10, 20, 50, 100];
  @Input() isPreselectingCheckboxes = false;
  @Output() selectedItemsChange = new EventEmitter<any[]>();
  @Output() page = new EventEmitter<any>();
  @Output() preselectionDone = new EventEmitter<void>();
  @Output() allTextSearch = new EventEmitter<string>();
  @Output() serverFilter = new EventEmitter<any>();
  filterAttributes = [
    {
      dataKeyName: 'addedOn',
      name: 'Added On',
      type: 'date',
      data: [],
    },
    {
      dataKeyName: 'status',
      name: 'Status',
      type: 'status',
      data: ['Active', 'Inactive', 'Deleted', 'Archived'],
    },
    {
      dataKeyName: 'full_name',
      name: 'Name',
      type: 'text',
      data: [],
    },
    {
      dataKeyName: 'username',
      name: 'Username',
      type: 'text',
      data: [],
    },
    {
      dataKeyName: 'custom_attrs',
      name: 'Attributes',
      type: 'list',
      data: [],
    },
    {
      dataKeyName: 'all',
      name: 'All Text',
      type: 'all',
      data: [],
    },
  ];

  dateRangeDetails = {
    isSingle: true,
    startDataKey: 'addedOn',
  };
  // candidatetable.component.ts
  @ViewChild('tableRef') tableComponent!: TableComponent;

  // Expose a manual toggle method
  public toggleRowFromParent(row: any, checked: boolean): void {
    const manualEvent = { checked };
    this.tableComponent.onClickCheckboxItem(manualEvent, row);
  }

  constructor(
    private route: Router,
    private exportService: ExportService,
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private apiCallService: ApiCallService,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.onRowSelectionChanged();
  }

  private _preSelectionApplied = false;
  ngOnChanges(changes: SimpleChanges): void {
      // Re-applying selection on every `selectedItems` change fights the user:
    // a click emits a new array, parent reassigns the input, ngOnChanges fires
    // and triggerPreSelection re-selects rows the user just unticked. Only
    // run pre-selection when:
    //   - the table first gets a non-empty selectedItems list (initial load), or
    //   - the dataSource changes (new rows arrived; need to apply existing selection).
    const selectedItemsChange = changes['selectedItems'];
    const dataSourceChange = changes['dataSource'];
    const preselectingChange = changes['isPreselectingCheckboxes'];

    if (dataSourceChange && this.tableComponent) {
      this._preSelectionApplied = false;
    }

    const shouldApply =
      !this._preSelectionApplied &&
      this.selectedItems &&
      this.selectedItems.length > 0 &&
      (selectedItemsChange || dataSourceChange);

    if (shouldApply) {
      setTimeout(() => {
        this.triggerPreSelection();
        this._preSelectionApplied = true;
      }, 100);
    } else if (preselectingChange?.currentValue === true) {
      // Loader was turned on but there is nothing to preselect (empty list or
      // already applied). Clear the loader immediately so it never gets stuck.
      this.preselectionDone.emit();
    }

    if (
      selectedItemsChange &&
      (!this.selectedItems || this.selectedItems.length === 0) &&
      this.tableComponent?.selection?.selected.length
    ) {
      this.tableComponent.selection.clear();
      this._preSelectionApplied = false;
    }
  }
  ngAfterViewInit(): void {
    if (this.selectedItems && this.selectedItems.length > 0) {
      setTimeout(() => this.triggerPreSelection(), 200);
    }
  }

  // Trigger pre-selection on the table component
  private triggerPreSelection(): void {
    if (!this.tableComponent) return;
    this.tableComponent.selection.clear();
    if (!this.selectedItems || this.selectedItems.length === 0) {
      this.preselectionDone.emit();
      return;
    }

    const currentData = this.tableComponent.tableDataSource?.data || [];
    // Match on any of the id/candidate_id permutations so selections carry
    // across data shapes from different sources.
    const selectedRows = currentData.filter((row: any) =>
      this.selectedItems.some((selected: any) =>
        (selected.id == row.id ||
         selected.candidate_id == row.id ||
         selected.id == row.candidate_id ||
         (selected.candidate_id && row.candidate_id && selected.candidate_id == row.candidate_id))
      )
    );

    // Run selection outside Angular zone to avoid per-item CD cycles,
    // then re-enter once to commit the final state.
    this.ngZone.runOutsideAngular(() => {
      if (selectedRows.length > 0) {
        this.tableComponent.selection.select(...selectedRows);
      }
      this.ngZone.run(() => {
        this.preselectionDone.emit();
      });
    });
  }

  rowAction(event: any) {
    this.rowActionCallBacks[event.rowName](event, event.rowName);
  }

  tableAction(event: any) {
    if (event === 'download') this.exportToExcel();
    else if (event === 'upload') this.uploadCandidates();
    else if (event?.type === 'serverFilter') this.serverFilter.emit(event.filters);
    else this.tabelActionCallBacks[event](event, this.dataSource);
  }
  toggleEmitterEvent(row, checked) {
    this.toggleEmitter(row, checked);
  }

  onSelectAllChanged(isChecked: boolean) {
    if (isChecked) {
      this.selectedItems = [...this.dataSource]; // or whatever your data source is
    } else {
      this.selectedItems = [];
    }
    this.selectedItemsChange.emit(this.selectedItems);
  }

  onRowSelectionChanged() {
    this.selectedItemsChange.emit(this.selectedItems);
  }
  exportToExcel() {
    const userOrgList = JSON.parse(localStorage.getItem('userOrgList'));
    const orgIdToNameMap = userOrgList.reduce((acc, org) => {
      acc[org.org_id] = org.org_name;
      return acc;
    }, {});

    const filterFieldsForEachCandidateInAList = this.dataSource.map((item) => {
      const candidate = omit(item, ['attributes']);
      const formatDate = (datetime) => {
        if (!datetime) return null;
        const date = new Date(datetime);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      candidate.created_datetime = formatDate(candidate.created_datetime);
      candidate.modified_datetime = formatDate(candidate.modified_datetime);

      candidate['organization_name'] =
        orgIdToNameMap[candidate.organization_id];
      candidate['is_archived'] = candidate['is_archived'] ? 'Yes' : 'No';
      candidate['is_deleted'] = candidate['is_deleted'] ? 'Yes' : 'No';
      candidate['is_active'] = candidate['is_active'] ? 'Yes' : 'No';
      candidate['status'] = candidate['status'] == 1 ? 'active' : 'inactive';
      delete candidate.organization_id;
      return candidate;
    });
    const fileName = `Candidates_${convertDate()}_${new Date().getTime()}`;
    this.exportService.exportExcel(
      filterFieldsForEachCandidateInAList,
      fileName
    );
  }

  uploadCandidates() {
    const dialogRef = this.dialog.open(UploadComponent, {
      width: 'min(1000px, 92vw)',
      maxHeight: '750px',
      minHeight: 'fit-content',
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: { type: 'candidates' },
    });
    dialogRef.afterClosed().subscribe((result) => {});
  }
}

